import Cookies from 'universal-cookie'
import { setStorage, removeItemStorage } from '~/utils/storage'

const cookie = new Cookies()

export const generateExpiredAtDate = (data) => {
  try {
    const expiredAtTimestamp =
      ((data?.created_at ?? 0) + (data?.expires_in ?? 0)) * 1000
    const expiredAtDate = new Date(expiredAtTimestamp).toISOString()
    return expiredAtDate
  } catch (err) {
    return ''
  }
}

export function setAuthStrategy() {
  const authStrategy = cookie.get('qontak.strategy')
  if (authStrategy !== 'hub') {
    cookie.set('qontak.strategy', 'hub', { path: '/' })
  }
}

/**
 * SSO cookie families shared across `*.qontak.*` apps via the parent domain
 * (HOST_URL). Every write and clear goes through this one table so the
 * families cannot drift apart — previously each family was written and
 * cleared from separate hand-rolled call sites, which let one set go stale
 * after a token rotation or an SSO sign-out.
 */
const SSO_COOKIE_FAMILIES = {
  global: {
    token: 'global_sso_token',
    refreshToken: 'global_sso_refresh_token',
    validUntil: 'global_sso_valid_until',
  },
  chat: {
    token: 'chat_sso_token',
    refreshToken: 'chat_sso_refresh_token',
    validUntil: 'chat_sso_valid_until',
  },
  crm: {
    token: 'crm_sso_token',
    refreshToken: 'crm_sso_refresh_token',
    validUntil: 'crm_sso_valid_until',
  },
}

const SSO_COOKIE_KEYS = Object.values(SSO_COOKIE_FAMILIES).flatMap((family) =>
  Object.values(family)
)

/**
 * Write one family's three cookies, or none of them. `setStorage` silently
 * skips falsy values, so without this guard a missing refresh token would
 * leave a new access token paired with the previous refresh token.
 */
function setSsoFamily(
  family,
  token,
  refreshToken,
  expiredAt,
  maxAgeInSeconds,
  maxAgeInSecondsOneMonth
) {
  if (!token || !refreshToken || !expiredAt) {
    return
  }

  const keys = SSO_COOKIE_FAMILIES[family]
  const domain = process.env.HOST_URL
  setStorage(keys.token, token, { domain, maxAge: maxAgeInSeconds })
  setStorage(keys.refreshToken, refreshToken, {
    domain,
    maxAge: maxAgeInSecondsOneMonth,
  })
  setStorage(keys.validUntil, expiredAt, { domain, maxAge: maxAgeInSeconds })
}

export function setChatToken(...args) {
  setSsoFamily('chat', ...args)
}

export function setCrmToken(...args) {
  setSsoFamily('crm', ...args)
}

export function setGlobalToken(...args) {
  setSsoFamily('global', ...args)
}

/**
 * @description Write every SSO cookie family for a freshly issued session in
 * one call. Global and chat always carry the Hub token; CRM is written only
 * when the response includes a complete `crm_token_attr` — otherwise the
 * existing CRM cookies are left as they are, since the CRM session is owned
 * and rotated by CRM.
 * Nothing is written if the Hub token, refresh token or expiry is missing, so
 * a malformed response cannot leave the families out of step.
 */
export function setSsoSession({
  token,
  refreshToken,
  expiredAt,
  maxAgeInSeconds,
  maxAgeInSecondsOneMonth,
  crmTokenAttr,
}) {
  if (!token || !refreshToken || !expiredAt) {
    return
  }

  setGlobalToken(
    token,
    refreshToken,
    expiredAt,
    maxAgeInSeconds,
    maxAgeInSecondsOneMonth
  )
  setChatToken(
    token,
    refreshToken,
    expiredAt,
    maxAgeInSeconds,
    maxAgeInSecondsOneMonth
  )

  if (crmTokenAttr?.access_token && crmTokenAttr?.refresh_token) {
    setCrmToken(
      crmTokenAttr.access_token,
      crmTokenAttr.refresh_token,
      expiredAt,
      maxAgeInSeconds,
      maxAgeInSecondsOneMonth
    )
  }
}

/**
 * @description Remove every SSO cookie family together. Use this instead of a
 * single-family remove so no family survives an SSO sign-out.
 */
export function clearSsoCookies() {
  SSO_COOKIE_KEYS.forEach((key) =>
    removeItemStorage(key, { domain: process.env.HOST_URL })
  )
}

/**
 * Cookies scoped to the parent domain (HOST_URL, e.g. `.qontak.com`).
 * A cookie is keyed by (name, domain, path) — removing these without the same
 * domain option leaves the parent-domain cookie in place, which is how a live
 * bearer token used to survive sign-out in `qontak._token.hub.share`.
 */
const SHARED_DOMAIN_COOKIES = [
  'qontak._token.hub.share',
  ...SSO_COOKIE_KEYS,
  'is_sync_auth',
]

/** Cookies written without a domain, so host-only. */
const HOST_COOKIES = [
  'qontak._token.hub',
  'qontak._refresh_token.hub',
  'qontak._token.hub.valid_until',
]

/**
 * nuxt-auth mirrors the token into localStorage as well as the cookie.
 * nuxt.config overrides `auth.cookie.prefix` to `qontak.` but leaves
 * `auth.localStorage.prefix` at its default, hence the two prefixes here.
 */
const LOCAL_STORAGE_KEYS = [
  'auth._token.hub',
  'auth._refresh_token.hub',
  'time_to_refresh',
  'mqtt-credentials',
  'subscribe_token',
]

/**
 * @description Remove every credential artifact this app writes for a session.
 * Single teardown for all sign-out paths — hand-rolled cleanup at each call
 * site is how keys drifted out of sync in the first place.
 */
export function clearAuthArtifacts() {
  HOST_COOKIES.forEach((key) => removeItemStorage(key))
  SHARED_DOMAIN_COOKIES.forEach((key) =>
    removeItemStorage(key, { domain: process.env.HOST_URL })
  )

  if (typeof localStorage === 'undefined') {
    return
  }

  LOCAL_STORAGE_KEYS.forEach((key) => {
    try {
      localStorage.removeItem(key)
    } catch (err) {
      // Storage blocked (private mode, disabled site data). Cookies are already
      // cleared, so there is nothing further to do for this key.
    }
  })
}

/**
 * @description Build the CRM half of the sign-out payload so the server can
 * revoke the linked CRM session, not just the Hub one.
 * @returns An object with crm_token (and crm_device_id when known), or empty.
 */
export function generateCrmSignoutPayload() {
  const crmSsoToken = cookie.get(SSO_COOKIE_FAMILIES.crm.token, {
    domain: process.env.HOST_URL,
  })
  const crmDeviceId = cookie.get('qcrm_device_id', {
    domain: process.env.HOST_URL,
  })

  const crmSignoutPayload = {}
  if (crmSsoToken) {
    crmSignoutPayload.crm_token = crmSsoToken
    if (crmDeviceId) {
      crmSignoutPayload.crm_device_id = crmDeviceId
    }
  }
  return crmSignoutPayload
}

export function initValidUntilCookie() {
  try {
    /**
     * To prevent refresh token when swithing after login from CRM
     */
    const timeToRefresh = cookie.get('chat_sso_valid_until')
    if (timeToRefresh && !localStorage.getItem('time_to_refresh')) {
      const decoded = decodeURIComponent(timeToRefresh)
      const tz = decoded.endsWith('Z') ? '' : 'Z'
      const date = new Date(decoded + tz)
      const dateTime = date.getTime()
      if (!isNaN(dateTime)) {
        localStorage.setItem('time_to_refresh', dateTime - 600)
      }
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Error initializing valid_until cookie:', err)
  }
}
